import { Card, Badge } from '@lineageatlas/design-system';
import { productConfig } from '../config/product';
import {
  EvidenceCitationList,
  EvidenceDrawerHost,
  QCCopilotAssistPanel,
  aiReferencesToEvidenceDrawerItems,
  normalizeAIEvidenceReferences,
  type AIFindingReferenceInput,
} from './ConsolePrimitives';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AIAnalysis {
  summary: string;
  risk_level: 'low' | 'medium' | 'high';
  findings: Array<{
    marker: string;
    status: string;
    detail: string;
    references?: AIFindingReferenceInput[];
  }>;
  recommendations: string[];
  confidence: number;
  references?: AIFindingReferenceInput[];
}

interface AIAnalysisCardProps {
  analysis: AIAnalysis | null | undefined;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function riskBadgeVariant(risk: AIAnalysis['risk_level']): 'excellent' | 'caution' | 'risk' {
  switch (risk) {
    case 'low':
      return 'excellent';
    case 'medium':
      return 'caution';
    case 'high':
      return 'risk';
  }
}

function riskLabel(risk: AIAnalysis['risk_level']): string {
  switch (risk) {
    case 'low':
      return 'Low Risk';
    case 'medium':
      return 'Medium Risk';
    case 'high':
      return 'High Risk';
  }
}

function findingIcon(status: string): string {
  switch (status.toLowerCase()) {
    case 'pass':
      return '✓';
    case 'warn':
    case 'warning':
      return '⚠';
    case 'fail':
      return '✕';
    default:
      return '·';
  }
}

function findingColor(status: string): string {
  switch (status.toLowerCase()) {
    case 'pass':
      return 'var(--color-accent-primary)';
    case 'warn':
    case 'warning':
      return 'var(--color-warning)';
    case 'fail':
      return 'var(--color-danger)';
    default:
      return 'var(--color-text-muted)';
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AIAnalysisCard({ analysis }: AIAnalysisCardProps) {
  if (!analysis) {
    return null;
  }

  const confidencePct = Math.round(analysis.confidence * 100);
  const references = normalizeAIEvidenceReferences([
    ...(analysis.references ?? []),
    ...analysis.findings.flatMap((finding) => finding.references ?? []),
  ]);
  const evidenceItems = aiReferencesToEvidenceDrawerItems(references);
  const reviewFocus = [
    ...analysis.findings
      .filter((finding) => ['warn', 'warning', 'fail'].includes(finding.status.toLowerCase()))
      .map(
        (finding) =>
          `${finding.marker}: verify ${finding.status.toLowerCase()} finding against source QC data.`,
      ),
    ...analysis.recommendations
      .slice(0, 2)
      .map((recommendation) => `Reviewer follow-up: ${recommendation}`),
  ];

  return (
    <div
      style={{
        marginTop: 'var(--space-4)',
        // Subtle gradient border to visually distinguish from regular cards
        background: 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(139,92,246,0.04) 100%)',
        borderRadius: 'var(--radius-lg)',
        padding: '1px',
      }}
      role="region"
      aria-label="AI QC Analysis"
    >
      <Card
        style={{
          borderRadius: 'calc(var(--radius-lg) - 1px)',
          background: 'var(--color-bg-surface)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 'var(--space-2)',
            marginBottom: 'var(--space-4)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <span aria-hidden="true" style={{ fontSize: 16, lineHeight: 1 }}>
              ✦
            </span>
            <span
              style={{
                fontSize: 'var(--text-subheading)',
                fontWeight: 700,
                color: 'var(--color-text)',
              }}
            >
              AI second-review analysis
            </span>
            <span
              style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--color-text-muted)',
                background: 'var(--color-bg-elevated)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-full)',
                padding: '1px 8px',
                fontStyle: 'italic',
              }}
            >
              {productConfig.aiDisclaimer} · does not release or hold lots
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <Badge status={riskBadgeVariant(analysis.risk_level)}>
              {riskLabel(analysis.risk_level)}
            </Badge>
            <span
              style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--color-text-muted)',
              }}
            >
              {confidencePct}% confidence
            </span>
          </div>
        </div>

        {/* Summary */}
        <p
          style={{
            fontSize: 'var(--text-body)',
            color: 'var(--color-text)',
            lineHeight: 1.6,
            margin: 0,
            marginBottom: 'var(--space-4)',
          }}
        >
          {analysis.summary}
        </p>

        {/* Findings */}
        {analysis.findings.length > 0 && (
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <div
              style={{
                fontSize: 'var(--text-xs)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'var(--color-text-muted)',
                marginBottom: 'var(--space-2)',
              }}
            >
              Findings with reviewer verification
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                gap: 'var(--space-2)',
              }}
            >
              {analysis.findings.map((finding, i) => (
                <div
                  key={`${finding.marker}-${i}`}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-2) var(--space-3)',
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--color-bg-elevated)',
                    border: '1px solid var(--color-border)',
                    fontSize: 'var(--text-caption)',
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      color: findingColor(finding.status),
                      fontWeight: 700,
                      flexShrink: 0,
                      marginTop: 1,
                    }}
                  >
                    {findingIcon(finding.status)}
                  </span>
                  <div>
                    <span
                      style={{
                        fontWeight: 600,
                        color: 'var(--color-text)',
                        fontFamily: 'var(--font-mono)',
                      }}
                    >
                      {finding.marker}
                    </span>
                    <span style={{ color: 'var(--color-text-muted)', marginLeft: 4 }}>
                      {finding.detail}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recommendations */}
        {analysis.recommendations.length > 0 && (
          <div>
            <div
              style={{
                fontSize: 'var(--text-xs)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'var(--color-text-muted)',
                marginBottom: 'var(--space-2)',
              }}
            >
              Suggested reviewer follow-up
            </div>
            <ol
              style={{
                margin: 0,
                paddingLeft: 'var(--space-5)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-1)',
              }}
            >
              {analysis.recommendations.map((rec, i) => (
                <li
                  key={i}
                  style={{
                    fontSize: 'var(--text-caption)',
                    color: 'var(--color-text)',
                    lineHeight: 1.5,
                  }}
                >
                  {rec}
                </li>
              ))}
            </ol>
          </div>
        )}

        {references.length > 0 && (
          <div style={{ marginTop: 'var(--space-4)' }}>
            <div
              style={{
                fontSize: 'var(--text-xs)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'var(--color-text-muted)',
                marginBottom: 'var(--space-2)',
              }}
            >
              Show your work
            </div>
            <EvidenceCitationList references={references} />
            <div style={{ marginTop: 'var(--space-3)' }}>
              <EvidenceDrawerHost title="AI evidence drawer" items={evidenceItems} />
            </div>
          </div>
        )}

        <QCCopilotAssistPanel reviewFocus={reviewFocus} preview />
      </Card>
    </div>
  );
}
